import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * Centred image lightbox. Escape or a click on the backdrop dismisses it; a
 * click on the image itself does not, so dragging to select or right-clicking
 * to save still works.
 *
 * Rendered through a portal because attachments live deep inside the chat
 * scroller: a `position: fixed` child is clipped by any ancestor carrying a
 * transform or filter, which would pin the overlay inside the message list
 * instead of over the window.
 *
 * Extracted from the agent sidecar's avatar preview so avatars and chat
 * attachments share one viewer rather than drifting apart.
 */
export function ImageLightbox({
  src,
  alt,
  onClose,
}: {
  src: string;
  alt: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-6 animate-in fade-in"
      onClick={onClose}
      role="dialog"
      aria-label={alt}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-md p-2 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
        aria-label="Close preview"
      >
        <X size={22} />
      </button>
      <img
        src={src}
        alt={alt}
        className="max-h-[85vh] max-w-[85vw] rounded-2xl object-contain shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body,
  );
}
